import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { resendOtp, verifyOtp } from "../api";
import { saveSession } from "../utils/session";

const RESEND_SECONDS = 30;

const digitBoxSx = (focused, filled) => ({
    width: { xs: 44, sm: 58 },
    height: { xs: 52, sm: 64 },
    border: `1px solid ${focused ? "#171715" : filled ? "#C9C6C0" : "#E0DED9"}`,
    background: "#FFFFFF",
    borderRadius: "3px",
    boxShadow: focused ? "0 0 0 3px #EAE8E3" : "none",
    fontSize: 26,
    fontWeight: 600,
    fontFamily: "inherit",
    color: "#171715",
    textAlign: "center",
    outline: "none",
    padding: 0,
    caretColor: "#171715",
});

/**
 * OTP verification step (Verify.dc.html): 6 digit inputs, resend countdown,
 * "Verify and sign in". On success stores the session and goes to /dashboard.
 */
export default function OtpStep({ email, pendingToken, setPendingToken, onBack }) {
    const navigate = useNavigate();
    const [digits, setDigits] = useState(["", "", "", "", "", ""]);
    const [focusIdx, setFocusIdx] = useState(0);
    const [seconds, setSeconds] = useState(RESEND_SECONDS);
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);
    const refs = useRef([]);

    useEffect(() => {
        refs.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (seconds <= 0) return undefined;
        const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [seconds]);

    const code = digits.join("");

    function setDigit(i, value) {
        setDigits((prev) => {
            const next = [...prev];
            next[i] = value;
            return next;
        });
    }

    function handleChange(i, e) {
        const raw = e.target.value.replace(/\D/g, "");
        if (!raw) {
            setDigit(i, "");
            return;
        }
        if (raw.length > 1) {
            /* A multi-char entry (e.g. paste mid-flow) fills forward. */
            fillFrom(i, raw);
            return;
        }
        setDigit(i, raw);
        if (i < 5) refs.current[i + 1]?.focus();
    }

    function fillFrom(start, raw) {
        setDigits((prev) => {
            const next = [...prev];
            for (let k = 0; k < raw.length && start + k < 6; k += 1) {
                next[start + k] = raw[k];
            }
            return next;
        });
        const last = Math.min(start + raw.length, 5);
        refs.current[last]?.focus();
    }

    function handleKeyDown(i, e) {
        if (e.key === "Backspace") {
            if (digits[i]) {
                setDigit(i, "");
            } else if (i > 0) {
                setDigit(i - 1, "");
                refs.current[i - 1]?.focus();
            }
            e.preventDefault();
        } else if (e.key === "ArrowLeft" && i > 0) {
            refs.current[i - 1]?.focus();
        } else if (e.key === "ArrowRight" && i < 5) {
            refs.current[i + 1]?.focus();
        }
    }

    function handlePaste(e) {
        const raw = (e.clipboardData.getData("text") || "")
            .replace(/\D/g, "")
            .slice(0, 6);
        if (!raw) return;
        e.preventDefault();
        fillFrom(0, raw);
    }

    async function handleVerify(e) {
        e?.preventDefault();
        if (code.length !== 6) {
            toast.error("Enter the 6-digit code");
            return;
        }
        setVerifying(true);
        try {
            const { data } = await verifyOtp(pendingToken, code);
            saveSession(data);
            navigate("/dashboard", { replace: true });
        } catch (err) {
            toast.error(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    "That code is wrong or has expired",
            );
        } finally {
            setVerifying(false);
        }
    }

    async function handleResend() {
        setResending(true);
        try {
            const { data } = await resendOtp(pendingToken);
            if (data?.pendingToken) setPendingToken(data.pendingToken);
            setDigits(["", "", "", "", "", ""]);
            setSeconds(RESEND_SECONDS);
            refs.current[0]?.focus();
            toast.success(data?.message || "A new code is on its way");
        } catch (err) {
            toast.error(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    "Could not resend the code",
            );
        } finally {
            setResending(false);
        }
    }

    return (
        <Stack
            component="form"
            onSubmit={handleVerify}
            sx={{ width: "100%", maxWidth: 440, gap: 4 }}
        >
            <Stack sx={{ gap: "12px" }}>
                <Stack
                    sx={{
                        width: 52,
                        height: 52,
                        border: "1px solid #D8D5CF",
                        borderRadius: "50%",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#FFFFFF",
                    }}
                >
                    <svg
                        width="22"
                        height="22"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="#171715"
                        strokeWidth="1.4"
                    >
                        <rect x="2" y="4" width="16" height="12" rx="1.5" />
                        <path d="m2.5 5.5 7.5 6 7.5-6" />
                    </svg>
                </Stack>
                <Typography
                    sx={{
                        fontFamily: "'Newsreader', Georgia, serif",
                        fontSize: 38,
                        fontWeight: 400,
                        letterSpacing: "-0.5px",
                    }}
                >
                    Check your inbox
                </Typography>
                <Typography
                    sx={{ fontSize: 15, color: "#6E6B66", lineHeight: 1.6 }}
                >
                    We sent a 6-digit code to{" "}
                    <Typography
                        component="span"
                        sx={{ fontWeight: 600, color: "#171715", fontSize: 15 }}
                    >
                        {email}
                    </Typography>
                    . Enter it below to continue.
                </Typography>
            </Stack>

            <Stack sx={{ gap: "14px" }}>
                <Typography
                    component="label"
                    sx={{
                        fontSize: "12.5px",
                        fontWeight: 600,
                        letterSpacing: "0.8px",
                        color: "#504D48",
                    }}
                >
                    VERIFICATION CODE
                </Typography>
                <Stack sx={{ flexDirection: "row", gap: "12px" }}>
                    {digits.map((d, i) => (
                        <Stack
                            key={i}
                            component="input"
                            ref={(el) => {
                                refs.current[i] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            autoComplete={i === 0 ? "one-time-code" : "off"}
                            value={d}
                            onChange={(e) => handleChange(i, e)}
                            onKeyDown={(e) => handleKeyDown(i, e)}
                            onPaste={handlePaste}
                            onFocus={() => setFocusIdx(i)}
                            sx={digitBoxSx(focusIdx === i, !!d)}
                        />
                    ))}
                </Stack>
                <Typography sx={{ fontSize: "13.5px", color: "#8B8781" }}>
                    Didn&#39;t get it?{" "}
                    {seconds > 0 ? (
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 600,
                                color: "#504D48",
                                fontSize: "13.5px",
                            }}
                        >
                            Resend in 0:{String(seconds).padStart(2, "0")}
                        </Typography>
                    ) : (
                        <Typography
                            component="span"
                            onClick={resending ? undefined : handleResend}
                            sx={{
                                fontWeight: 600,
                                color: "#1E6B4F",
                                fontSize: "13.5px",
                                cursor: "pointer",
                                opacity: resending ? 0.6 : 1,
                            }}
                        >
                            {resending ? "Resending…" : "Resend code"}
                        </Typography>
                    )}
                </Typography>
            </Stack>

            <Stack sx={{ gap: "18px" }}>
                <Stack
                    component="button"
                    type="submit"
                    disabled={verifying}
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        background: "#171715",
                        color: "#FAFAF9",
                        height: 54,
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: "0.3px",
                        borderRadius: "3px",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        opacity: verifying ? 0.7 : 1,
                        "&:hover": { background: "#2B2A27" },
                    }}
                >
                    {verifying ? "Verifying…" : "Verify and sign in"}
                    <svg
                        width="15"
                        height="15"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="#FAFAF9"
                        strokeWidth="1.6"
                    >
                        <path d="M2 8h11M9 3.5 13.5 8 9 12.5" />
                    </svg>
                </Stack>
                <Stack
                    component="button"
                    type="button"
                    onClick={onBack}
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#6E6B66",
                        height: 44,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        "&:hover": { color: "#171715" },
                    }}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                    >
                        <path d="M14 8H3M7 3.5 2.5 8 7 12.5" />
                    </svg>
                    Use a different email
                </Stack>
            </Stack>
        </Stack>
    );
}
